<template>
  <div class="combobox-filter-inline-label">
    <v-select
      :class="[
        shiftIsPressed ? 'v-select--shift-is-pressed' : '',
        valuesArr && valuesArr.length ? 'v-select--has-selection' : '',
        !isBlured ? 'v-select--label-is-search' : ''
      ]"
      ref="combobox2000"
      v-model="valuesArr"
      :items="filteredItems"
      v-bind="attrsWithoutItems"
      multiple
      @change="onChange"
      @click:append="() => Clear()"
      :allow-overflow="false"
      return-object
      append-icon="mdi-close"
      @keydown="keydownHandler"
      @keyup="keyupHandler"
      @paste="onPaste"
      @copy="onCopy"
      v-on:blur="onBlur"
      @focus="onFocus"
      :menu-props="computedMenuProps"
    >
      <template v-slot:label>
        <span v-if="isBlured">{{ $attrs.label }}</span>
        <span v-else class="combobox-filter-search-text">
          {{ selectionLabel }}{{ localSearch }}<span v-if="localSearch" class="combobox-filter-caret" aria-hidden="true"></span>
        </span>
      </template>
      <template v-slot:prepend-item v-if="$attrs.prependStatuses">
        <v-list-item
          v-if="
            $attrs.prependText != undefined &&
              $attrs.prependText != null &&
              $attrs.prependText != ''
          "
          ripple
          @click="prependStatuses"
          :class="allActiveIsSelected ? 'font-weight-bold' : ''"
        >
          <v-list-item-action class="ma-0 pa-0 mr-2">
            <v-icon>{{ $attrs.prependIcon }}</v-icon>
          </v-list-item-action>
          <v-list-item-content class="ma-0 pa-0">
            <v-list-item-title
              :style="allActiveIsSelected ? 'font-weight-bold' : ''"
              >{{ $attrs.prependText }}</v-list-item-title
            >
          </v-list-item-content>
        </v-list-item>
        <v-list-item
          ripple
          @click="allStatuses"
          :class="allIsSelected ? 'font-weight-bold' : ''"
        >
          <v-list-item-action class="ma-0 pa-0 mr-2">
            <v-icon>{{ $attrs.prependIcon }}</v-icon>
          </v-list-item-action>
          <v-list-item-content class="ma-0 pa-0">
            <v-list-item-title
              :style="allIsSelected ? 'font-weight-bold' : ''"
              >{{ $attrs.prependTextSelectAll }}</v-list-item-title
            >
          </v-list-item-content>
        </v-list-item>
        <v-divider class="mt-2"></v-divider>
      </template>
      <template v-slot:selection="{ item, index }">
        <div class="combobox-filter-selection-item">
          <span v-if="index < displayCount">
            {{
              $props.translate
                ? $props.localizationFunc(item)
                : item[$attrs.itemText]
            }}
          </span>
          <span v-if="index === displayCount">
            (+{{ $t("nOthers", [valuesArr.length - index]) }})
          </span>
        </div>
      </template>
      <template v-slot:item="{ item }">
        <span v-if="$props.translate">{{ $props.localizationFunc(item) }}</span>
        <span v-else>{{ item[$attrs.itemText] }}</span>
      </template>
    </v-select>
  </div>
</template>

<script>
export default {
  inheritAttrs: false,
  model: {
    prop: 'value',
    event: 'valueEv',
  },
  props: {
    value: Array,
    translate: {
      type: Boolean,
      default: false,
    },
    localizationFunc: {
      type: Function,
    },
    displayCount: {
      type: Number,
      default: 1,
    },
  },
  data() {
    return {
      valuesArr: [],
      shiftIsPressed: false,
      shiftKeyHeld: false,
      shiftFirstIndex: -1,
      shiftSecondIndex: -1,
      localSearch: '',
      isBlured: true,
      activeStatuses: ['waiting', 'process', 'pending', 'error', 'pause'],
    };
  },
  computed: {
    attrsWithoutItems() {
      const rest = { ...this.$attrs };

      delete rest.items;

      return rest;
    },
    filteredItems() {
      const items = this.$attrs.items || [];
      const q = (this.localSearch || '').toLowerCase().trim();

      if (!q) return items;

      const itemText = this.$attrs.itemText || 'text';
      const getText = (item) => {
        if (item == null) return '';

        if (this.translate && this.localizationFunc) return String(this.localizationFunc(item));

        if (typeof item === 'object') return String(item[itemText] ?? '');

        return String(item);
      };

      return items.filter((item) => getText(item).toLowerCase().includes(q));
    },
    computedMenuProps() {
      return {
        closeOnClick: false,
        closeOnContentClick: false,
        offsetY: true,
        nudgeBottom: 1
      };
    },
    allActiveIsSelected() {
      const activeStatusesCount = this.$attrs.items.filter((item) =>
        this.activeStatuses.some((statusName) =>
          item.statusName.toLowerCase().includes(statusName)
        )
      ).length;

      const selectedActiveStatusesCount = this.valuesArr.filter(
        (selectedItem) =>
          this.activeStatuses.some((statusName) =>
            selectedItem.statusName.toLowerCase().includes(statusName)
          )
      ).length;

      return (
        activeStatusesCount === selectedActiveStatusesCount &&
        !this.allIsSelected
      );
    },
    allIsSelected() {
      return this.$attrs.items.length === this.valuesArr.length;
    },
    selectionLabel() {
      if (!this.valuesArr || this.valuesArr.length === 0) return '';
      const itemText = this.$attrs.itemText || 'text';
      const getText = (item) => {
        if (item == null) return '';
        if (this.translate && this.localizationFunc) return String(this.localizationFunc(item));
        if (typeof item === 'object') return String(item[itemText] ?? '');

        return String(item);
      };
      const n = this.displayCount || 1;
      const parts = this.valuesArr.slice(0, n).map(getText);
      const rest = this.valuesArr.length - n;

      return rest > 0 ? parts.join(', ') + ` (+${rest}) ` : parts.join(', ') + ' ';
    },
  },
  mounted() {
    this.valuesArr = this.$props.value ?? [];
    document.addEventListener('keydown', this.shiftKeyDownHandler);
    document.addEventListener('keyup', this.shiftKeyUpHandler);
  },
  beforeDestroy() {
    document.removeEventListener('keydown', this.shiftKeyDownHandler);
    document.removeEventListener('keyup', this.shiftKeyUpHandler);
  },
  watch: {
    value() {
      this.valuesArr = this.value;
    },
  },
  methods: {
    onFocus() {
      this.isBlured = false;
      if (this.valuesArr && this.valuesArr.length > 0) {
        this.localSearch = '';
        this.$nextTick(() => {
          if (this.$refs.combobox2000) {
            this.$refs.combobox2000.isMenuActive = true;
          }
        });
      }
    },
    onBlur() {
      this.isBlured = true;
      this.localSearch = '';
      this.$emit('valueEv', this.valuesArr);
      this.$emit('blur');
      this.$emit('input', this.valuesArr);
    },
    getActiveStatuses() {
      const activeStatusesList = this.$attrs.items.filter((item) =>
        this.activeStatuses.some((statusName) =>
          item.statusName.toLowerCase().includes(statusName)
        )
      );

      return activeStatusesList;
    },
    allStatuses() {
      if (this.allIsSelected) {
        this.Clear();
      } else {
        this.valuesArr = this.getAllStatuses();
        this.onBlur();
      }
    },
    getAllStatuses() {
      const allStatuses = this.$attrs.items.filter(() => true);

      return allStatuses;
    },
    prependStatuses() {
      if (this.allActiveIsSelected) {
        this.valuesArr = this.valuesArr.filter(
          (item) =>
            !this.activeStatuses.some((statusName) =>
              item.statusName.toLowerCase().includes(statusName)
            )
        );
      } else {
        this.valuesArr = this.getActiveStatuses();
        this.onBlur();
      }
    },
    Clear() {
      this.valuesArr = [];
      this.onBlur();
    },
    onChange(val) {
      const items = this.filteredItems;
      if (this.shiftKeyHeld && items && items.length && val.length >= 2) {
        const itemValue = this.$attrs.itemValue;
        const startItem = val[val.length - 2];
        const endItem = val[val.length - 1];

        const findIndex = (list, obj) => {
          if (itemValue != null) {
            const key = typeof obj === 'object' ? obj[itemValue] : obj;

            return list.findIndex((item) => (typeof item === 'object' ? item[itemValue] : item) === key);
          }

          return list.indexOf(obj);
        };

        let startIdx = findIndex(items, startItem);
        let endIdx = findIndex(items, endItem);

        if (startIdx === -1) startIdx = endIdx;
        if (endIdx === -1) endIdx = startIdx;

        const from = Math.min(startIdx, endIdx);
        const to = Math.max(startIdx, endIdx);
        const range = items.slice(from, to + 1);

        this.valuesArr = range;
        this.$emit('input', range);
        this.localSearch = '';
        if (this.isBlured && this.$refs.combobox2000) {
          this.$refs.combobox2000.focus();
        }
        this.$nextTick(() => {
          if (this.$refs.combobox2000) this.$refs.combobox2000.isMenuActive = true;
        });

        return;
      }

      this.$emit('input', val);
      this.localSearch = '';
      if (this.isBlured && this.$refs.combobox2000) {
        this.$refs.combobox2000.focus();
      }
      this.$nextTick(() => {
        if (this.$refs.combobox2000) this.$refs.combobox2000.isMenuActive = true;
      });
    },
    shiftKeyDownHandler(event) {
      if (event.key === 'Shift' && !this.shiftKeyHeld) {
        this.shiftKeyHeld = true;
      }
    },
    shiftKeyUpHandler(event) {
      if (event.key === 'Shift' && this.shiftKeyHeld) {
        this.shiftKeyHeld = false;
      }
    },
    keydownHandler(event) {
      if (event.key === 'Shift' && !this.shiftIsPressed) {
        this.shiftIsPressed = true;
      }

      if (!this.isBlured) {
        if (event.key === 'Backspace') {
          event.preventDefault();
          if (this.localSearch && this.localSearch.length > 0) {
            this.localSearch = this.localSearch.slice(0, -1);
          } else if (this.valuesArr && this.valuesArr.length > 0) {
            this.valuesArr = this.valuesArr.slice(0, -1);
            this.$emit('input', this.valuesArr);
          }
        } else if (
          event.key.length === 1 &&
          !event.ctrlKey &&
          !event.metaKey &&
          !event.altKey &&
          !['Enter', 'Escape', 'Tab', 'ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(event.key)
        ) {
          event.preventDefault();
          this.localSearch = (this.localSearch || '') + event.key;
        }
      }
    },
    keyupHandler(event) {
      if (event.key === 'Shift' && this.shiftIsPressed) {
        this.shiftIsPressed = false;
      }
    },
    onPaste(event) {
      if (this.isBlured) return;
      event.preventDefault();
      const text = (event.clipboardData || window.clipboardData).getData('text');
      if (text) this.localSearch = (this.localSearch || '') + text;
    },
    onCopy(event) {
      if (this.isBlured) return;
      const text = this.localSearch || '';
      if (text && event.clipboardData) {
        event.preventDefault();
        event.clipboardData.setData('text/plain', text);
      }
    },
  },
};
</script>
<style>
.darkBackground {
  background: #363636;
  color: white;
}
.v-list-item {
  min-height: fit-content;
}
.v-select--shift-is-pressed {
  font-weight: bold;
}
.v-select--has-selection:not(.v-select--label-is-search) .v-label {
  display: none;
}
.combobox-filter-inline-label .v-select.v-select--label-is-search .v-select__selection,
.combobox-filter-inline-label .v-select.v-select--label-is-search .v-select__selections,
.combobox-filter-inline-label .v-select.v-select--label-is-search .v-select__selection *,
.combobox-filter-inline-label .v-select.v-select--label-is-search .v-select__selections * {
  color: transparent !important;
  -webkit-text-fill-color: transparent !important;
}
.combobox-filter-inline-label .v-select.v-select--label-is-search .v-label {
  z-index: 1;
  position: relative;
  pointer-events: none;
}
.combobox-filter-inline-label .v-select.v-select--label-is-search input {
  caret-color: transparent;
  color: transparent;
}
.combobox-filter-inline-label .v-select .v-label {
  top: 50% !important;
  transform: translateY(-50%);
}
/* Обрезка длинного ввода: одна строка, многоточие*/
.combobox-filter-inline-label {
  min-width: 0;
}
.combobox-filter-inline-label .v-input__slot {
  min-width: 0 !important;
}
.combobox-filter-inline-label .v-select__selections {
  flex-wrap: nowrap !important;
  min-width: 0;
  overflow: hidden !important;
}
.combobox-filter-inline-label .v-select__selection,
.combobox-filter-inline-label .combobox-filter-selection-item,
.combobox-filter-inline-label .v-select__selections > *,
.combobox-filter-inline-label .v-select__selection > * {
  overflow: hidden !important;
  text-overflow: ellipsis;
  white-space: nowrap !important;
  min-width: 0;
}
.combobox-filter-inline-label .v-select .v-label,
.combobox-filter-inline-label .v-select .v-label * {
  overflow: hidden !important;
  text-overflow: ellipsis;
  white-space: nowrap !important;
  min-width: 0;
}
.combobox-filter-search-text {
  white-space: nowrap !important;
  overflow: hidden;
  text-overflow: ellipsis;
  display: block;
  min-width: 0;
}
.combobox-filter-caret {
  display: inline-block;
  width: 2px;
  height: 1em;
  margin-left: 1px;
  vertical-align: text-bottom;
  background: currentColor;
  animation: combobox-filter-caret-blink 1s step-end infinite;
}
@keyframes combobox-filter-caret-blink {
  50% {
    opacity: 0;
  }
}
</style>
